import { router } from '@inertiajs/react'
import DashboardLayout from '@/components/DashboardLayout'
import { Card, CardContent } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Waves, Plus, Pencil, Archive, RotateCcw } from 'lucide-react'
import type { Equipment, BoardType, EquipmentKind } from '@/types/equipment'
import { boardTypeLabel, kindLabel } from '@/lib/equipment'

interface Props {
  equipments: Equipment[]
  filters: {
    kind: EquipmentKind | null
    board_type: BoardType | null
    archived: boolean
  }
  board_types: BoardType[]
  kinds: EquipmentKind[]
}

export default function EquipmentsIndex({ equipments, filters, board_types }: Props) {
  const totalUnidades = equipments.reduce((sum, e) => sum + e.quantity, 0)

  const updateFilter = (patch: Partial<Props['filters']>) => {
    const next = { ...filters, ...patch }
    router.get('/equipments', {
      kind: next.kind || undefined,
      board_type: next.board_type || undefined,
      archived: next.archived ? 'true' : undefined
    }, { preserveState: true, preserveScroll: true })
  }

  const handleArchive = (id: number, label: string) => {
    if (confirm(`Arquivar "${label}"? Ele sai da lista ativa mas o histórico é mantido.`)) {
      router.delete(`/equipments/${id}`)
    }
  }

  const handleUnarchive = (id: number) => {
    router.patch(`/equipments/${id}/unarchive`)
  }

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-8 max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
              <Waves className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Equipamentos</h1>
              <p className="text-sm text-muted-foreground">
                {equipments.length} {equipments.length === 1 ? 'item' : 'itens'} · {totalUnidades} {totalUnidades === 1 ? 'unidade' : 'unidades'}
              </p>
            </div>
          </div>
          <Button onClick={() => router.visit('/equipments/new')}>
            <Plus className="h-4 w-4 mr-1" />
            Novo Equipamento
          </Button>
        </div>

        <Card>
          <CardContent className="p-4 flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[180px]">
              <Label className="text-xs text-muted-foreground">Tipo de prancha</Label>
              <Select
                value={filters.board_type || 'all'}
                onValueChange={(v) => updateFilter({ board_type: v === 'all' ? null : (v as BoardType) })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {board_types.map((bt) => (
                    <SelectItem key={bt} value={bt}>{boardTypeLabel(bt)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2 pb-1">
              <Switch
                id="show-archived"
                checked={filters.archived}
                onCheckedChange={(checked) => updateFilter({ archived: checked })}
              />
              <Label htmlFor="show-archived" className="cursor-pointer">Mostrar arquivados</Label>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Tamanho</TableHead>
                  <TableHead className="text-center">Quantidade</TableHead>
                  <TableHead>Apelido</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {equipments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12">
                      <div className="flex flex-col items-center gap-2 text-muted-foreground">
                        <Waves className="h-10 w-10" />
                        <p>{filters.archived ? 'Nenhum equipamento arquivado' : 'Nenhum equipamento cadastrado'}</p>
                        {!filters.archived && (
                          <Button size="sm" onClick={() => router.visit('/equipments/new')}>
                            <Plus className="h-4 w-4 mr-1" />
                            Cadastrar primeira prancha
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  equipments.map((eq) => (
                    <TableRow key={eq.id} className={eq.archived ? 'opacity-60' : ''}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">{kindLabel(eq.kind)}</Badge>
                          <span>{boardTypeLabel(eq.board_type)}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono">{eq.size || '—'}</TableCell>
                      <TableCell className="text-center font-semibold">{eq.quantity}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {eq.nickname || '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          {eq.archived ? (
                            <Button variant="ghost" size="sm" onClick={() => handleUnarchive(eq.id)}>
                              <RotateCcw className="h-4 w-4 mr-1" />
                              Restaurar
                            </Button>
                          ) : (
                            <>
                              <Button variant="ghost" size="icon" onClick={() => router.visit(`/equipments/${eq.id}/edit`)}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleArchive(eq.id, `${boardTypeLabel(eq.board_type)} ${eq.size || ''}`.trim())}
                              >
                                <Archive className="h-4 w-4 text-destructive" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}
